import { SearchOutlined } from '@ant-design/icons';
import { Input, Segmented, Select as AntSelect, Space, Typography } from 'antd';
import type { ReactNode } from 'react';

/** Page title block with right-aligned actions (Ant typography; keeps the legacy class names for page CSS and tests). */
export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
      <div>
        <Typography.Title level={3} className="page-title" style={{ margin: 0 }}>{title}</Typography.Title>
        {subtitle ? <div className="page-subtitle">{subtitle}</div> : null}
      </div>
      {actions ? <Space wrap className="page-actions">{actions}</Space> : null}
    </div>
  );
}

/** Horizontal filter strip; children are chips, selects and a search box. */
export function FilterBar({ children }: { children: ReactNode }) {
  return <div className="filter-bar" role="group" aria-label="Filters">{children}</div>;
}
export const FilterSpacer = () => <span className="filter-spacer" />;

/** Single-select chips for state filters; plain buttons keep accessible names and keyboard behaviour, CSS gives the pill look. */
export function FilterChips<T extends string>({ options, value, onChange, label }: { options: readonly T[]; value: T; onChange: (v: T) => void; label?: string }) {
  return (
    <Space size={4} wrap role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o} type="button" className={`filter-chip${o === value ? ' active' : ''}`} aria-pressed={o === value} onClick={() => onChange(o)}>{o}</button>
      ))}
    </Space>
  );
}

/** Dropdown filter (Ant Select). The aria-label lands on the combobox so `getByRole('combobox', { name })` keeps working. */
export function FilterSelect<T extends string>({ label, value, onChange, options, width = 170 }: { label: string; value: T; onChange: (v: T) => void; options: { value: T; label: string }[]; width?: number }) {
  return <AntSelect<T> aria-label={label} value={value} onChange={onChange} options={options} style={{ minWidth: width }} popupMatchSelectWidth={false} virtual={false} />;
}

export function FilterSearch({ value, onChange, placeholder = 'Search…', label = 'Search' }: { value: string; onChange: (v: string) => void; placeholder?: string; label?: string }) {
  return <Input allowClear prefix={<SearchOutlined style={{ color: 'var(--text-muted)' }} />} aria-label={label} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} style={{ width: 240 }} />;
}

/** List/Kanban style switch (Ant Segmented); options keep their labels as accessible names. */
export function ViewToggle<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: { value: T; label: string }[] }) {
  return <Segmented<T> className="view-toggle" value={value} onChange={onChange} options={options} />;
}
